// @flow
import React from "react";
import { t } from "ttag";
import { getInternalOrExternal } from "../../utils/link/link";
import { Container } from "reactstrap";
import {
    Logo,
    Navigation,
    FacebookStroke,
    InstagramStroke,
    XStroke,
    LinkedinStroke,
    Typography,
} from "@2po-c21/components";
import style from "./style.module.scss";
import Link from "../Link";
import Tracker from "../Tracker/Tracker";
import Topbar from "../TopBar";
import { EVENT_MAP, type NavigationClickEvent } from "../../models/events";

export type NavigationItem = {
    title: string,
    navigationItems?: Array<NavigationItem>,
    id: string,
    parentTitle: string,
    mobileSubmenu: boolean,
    link?: *,
};

type Props = {
    navigationItems: Array<NavigationItem>,
    locale: string,
    translationSlugs: { [key: string]: string },
    isAgencyPage?: any,
    agencyId?: string,
    agencyName?: string,
    estimationTool?: boolean,
    topNavigationItems?: Array<NavigationItem>,
    socialLinks?: Array<*>,
    facebookUrl?: string,
};

export const getSocialIcon = (type) => {
    // eslint-disable-next-line smells/no-switch
    switch (type?.toLowerCase()) {
        case "facebook":
            return FacebookStroke;
        case "instagram":
            return InstagramStroke;
        case "x":
        case "twitter":
            return XStroke;
        case "linkedin":
            return LinkedinStroke;
        default:
            return FacebookStroke;
    }
};

const Header = ({
    navigationItems,
    locale,
    translationSlugs,
    isAgencyPage,
    agencyId,
    agencyName,
    estimationTool,
    topNavigationItems,
    socialLinks,
    facebookUrl,
}: Props) => {
    const getPrimaryLink = (item) => {
        const primaryLink = item?.primaryLink;
        return (
            primaryLink &&
            (primaryLink.contentfulInternalLink?.slug ||
                primaryLink.query ||
                primaryLink.externalLink)
        );
    };
    const navigationTree = navigationItems.map((item) => ({
        title: item.title,
        id: item.id,
        slug: getInternalOrExternal(item),
        primaryLink: getPrimaryLink(item),
        primaryLabel: item?.primaryLink?.linkTitle,
        navigationItems: item.navigationItems?.map((subItem) => ({
            title: subItem.title,
            id: subItem.id,
            slug: getInternalOrExternal(subItem),
            parentTitle: item.title,
            mobileSubmenu: subItem.mobileSubmenu,
            primaryLink: subItem.primaryLink,
            primaryLabel: subItem?.primaryLink?.linkTitle,
            navigationItems: subItem.navigationItems?.map((subSubItem) => ({
                title: subSubItem.title,
                slug: getInternalOrExternal(subSubItem),
                parentTitle: subItem.title,
            })),
        })),
        mobileSubmenu: item.mobileSubmenu,
    }));

    const LinkComponent = ({ children, slug }: *) => {
        if (slug && agencyId !== "" && slug.includes("agency=[agencyID]")) {
            slug = slug.replace("[agencyID]", agencyId);
        }

        const pageurl =
            typeof window !== "undefined" ? window.location.href : "";
        const trackerEvent: NavigationClickEvent = {
            type: EVENT_MAP.NavigationClickEvent,
            linkname: "",
            name: "main",
            pageurl: pageurl,
            targeturl: slug,
        };
        return slug ? (
            <Tracker trackerEvent={trackerEvent}>
                <Link className="gatsbyLink" to={slug}>
                    {children}
                </Link>
            </Tracker>
        ) : (
            <>{children}</>
        );
    };

    const shortAgencyName = agencyName.replace("CENTURY 21 ", "");

    // Create agencySocialLinks when on agency page
    const effectiveSocialLinks =
        isAgencyPage && facebookUrl
            ? [{ linkTitle: "Facebook", externalLink: facebookUrl }]
            : socialLinks || []; // Ensure we always return an array

    return (
        <>
            <div className={style.globalNavigationWrapper}>
                <Container>
                    <Topbar
                        socialLinks={effectiveSocialLinks}
                        topNavigationItems={topNavigationItems}
                        translationSlugs={translationSlugs}
                        LinkComponent={LinkComponent}
                        locale={locale}
                    />
                </Container>
            </div>
            <div className={isAgencyPage ? style.headerAgency : style.header}>
                <div className={style.navigationItemsHolder}>
                    <Container
                        className={style.container}
                        style={{ justifyContent: "space-between" }}
                    >
                        {isAgencyPage ? (
                            <div className={style.agencyLogoWrapper}>
                                <div
                                    className={`${style.logo} ${style.agencyLogo}`}
                                >
                                    <Logo
                                        logo="default"
                                        width="240"
                                        height="20"
                                    />
                                    <Typography type="heading-6-bold">
                                        {shortAgencyName}
                                    </Typography>
                                </div>
                            </div>
                        ) : (
                            <Link to={`/${locale}`}>
                                <div className={style.logo}>
                                    <Logo
                                        logo="benelux"
                                        width="240"
                                        height="20"
                                    />
                                </div>
                            </Link>
                        )}
                        <Navigation
                            navigationItems={navigationTree}
                            LinkComponent={LinkComponent}
                            estimationTool={!!estimationTool}
                            estimationLink={
                                isAgencyPage
                                    ? `/${locale}/${t`slug.estimation-tool`}?agency=${agencyId}`
                                    : `/${locale}/${t`slug.estimation-tool`}`
                            }
                            estimationLabel={t`label.estimation-tool`}
                            isAgencyPage={isAgencyPage}
                        >
                            <Topbar
                                socialLinks={effectiveSocialLinks}
                                topNavigationItems={topNavigationItems}
                                translationSlugs={translationSlugs}
                                LinkComponent={LinkComponent}
                                locale={locale}
                            />
                        </Navigation>
                    </Container>
                </div>
            </div>
        </>
    );
};

export default Header;
